import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import ResponsiveImage from './ResponsiveImage';
import ResponsiveVideo from './ResponsiveVideo';
import { galleryItemFromUrl } from '../utils/galleryMedia';

const SWIPE_MIN = 50;

/**
 * Two-step gallery: (1) grid / list of photos & videos — (2) full-screen black lightbox.
 *
 * @param {string[]} images — photo and/or video URLs
 * @param {string} [defaultPoster] — poster for video tiles in grid / lightbox
 * @param {string[]} [slideTitles] — optional per-slide title in lightbox
 */
export default function PhotoGalleryModal({
  open,
  onClose,
  images,
  venueTitle,
  initialIndex = 0,
  slideTitles,
  defaultPoster
}) {
  const items = useMemo(() => {
    const raw = (Array.isArray(images) ? images : []).filter((u) => u && String(u).trim());
    return raw.map((url) => galleryItemFromUrl(url, defaultPoster));
  }, [images, defaultPoster]);

  const [phase, setPhase] = useState('grid');
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef(null);
  const lightboxVideoRef = useRef(null);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setPhase('grid');
    const max = Math.max(0, items.length - 1);
    setActiveIndex(Math.min(Math.max(0, initialIndex), max));
  }, [open, initialIndex, items.length]);

  useEffect(() => {
    if (!open || phase !== 'lightbox') return undefined;
    const node = lightboxVideoRef.current;
    if (!node || items[activeIndex]?.type !== 'video') return undefined;
    node.muted = true;
    node.setAttribute('muted', '');
    node.setAttribute('playsinline', '');
    node.setAttribute('webkit-playsinline', 'true');
    const p = node.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
    return () => {
      try {
        node.pause();
      } catch (_) {
        /* ignore */
      }
    };
  }, [open, phase, activeIndex, items]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (phase === 'lightbox') setPhase('grid');
        else onClose();
        return;
      }
      if (phase !== 'lightbox' || items.length <= 1) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % items.length);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, phase, items.length, onClose]);

  const openLightbox = useCallback((index) => {
    setActiveIndex(index);
    setPhase('lightbox');
  }, []);

  const goNext = useCallback(() => {
    if (items.length <= 1) return;
    setActiveIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  const goPrev = useCallback(() => {
    if (items.length <= 1) return;
    setActiveIndex((i) => (i - 1 + items.length) % items.length);
  }, [items.length]);

  const onLightboxTouchStart = useCallback((e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  }, []);

  const onLightboxTouchEnd = useCallback(
    (e) => {
      if (touchStartX.current == null || items.length <= 1) return;
      const end = e.changedTouches[0]?.clientX;
      if (end == null) return;
      const d = touchStartX.current - end;
      if (d > SWIPE_MIN) goNext();
      else if (d < -SWIPE_MIN) goPrev();
      touchStartX.current = null;
    },
    [items.length, goNext, goPrev]
  );

  const lightboxCaption = useCallback(() => {
    const t = slideTitles && slideTitles[activeIndex];
    if (t != null && String(t).trim()) return String(t).trim();
    const item = items[activeIndex];
    if (item?.type === 'video') return `Video ${activeIndex + 1}`;
    return `Photo ${activeIndex + 1}`;
  }, [slideTitles, activeIndex, items]);

  if (!open || items.length === 0) return null;

  const activeItem = items[activeIndex];

  if (phase === 'lightbox') {
    return (
      <div
        className="fixed inset-0 z-[110] flex flex-col bg-black"
        role="dialog"
        aria-modal="true"
        aria-label="Gallery viewer"
      >
        <header className="grid shrink-0 grid-cols-[48px_1fr_48px] items-center gap-1 px-2 pt-[max(1rem,env(safe-area-inset-top))] pb-2 text-white">
          <button
            type="button"
            onClick={() => setPhase('grid')}
            className="flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/10"
            aria-label="Back to all photos"
          >
            <X className="h-6 w-6" strokeWidth={2} />
          </button>
          <h2 className="min-w-0 truncate text-center text-[17px] font-medium tracking-tight text-white">
            {lightboxCaption()}
          </h2>
          <span aria-hidden className="inline-block w-11" />
        </header>

        <div
          className="relative flex min-h-0 flex-1 items-center justify-center px-3"
          onTouchStart={onLightboxTouchStart}
          onTouchEnd={onLightboxTouchEnd}
        >
          {items.length > 1 ? (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-2 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 md:block"
                aria-label="Previous"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-2 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 md:block"
                aria-label="Next"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          ) : null}

          <div className="flex max-h-full max-w-full items-center justify-center">
            {activeItem?.type === 'video' ? (
              <ResponsiveVideo
                key={activeItem.url}
                ref={lightboxVideoRef}
                src={activeItem.url}
                poster={activeItem.poster || defaultPoster}
                deliveryWidth={900}
                className="max-h-[min(78dvh,900px)] max-w-full object-contain"
                muted
                playsInline
                autoPlay
                controls
                preload="auto"
                disablePictureInPicture
              />
            ) : (
              <ResponsiveImage
                key={activeIndex}
                src={activeItem.url}
                variant="gallery"
                priority
                alt={lightboxCaption()}
                className="max-h-[min(78dvh,900px)] max-w-full object-contain"
              />
            )}
          </div>
        </div>

        <footer className="shrink-0 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-2 text-center text-[15px] font-medium text-white">
          {activeIndex + 1}/{items.length}
        </footer>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-white md:flex-row md:bg-transparent"
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
    >
      <button
        type="button"
        className="hidden flex-1 bg-black/45 md:block"
        onClick={onClose}
        aria-label="Close gallery"
      />

      <div className="flex h-full w-full flex-col bg-white shadow-none md:max-h-full md:w-full md:max-w-[min(720px,58vw)] md:shrink-0 md:shadow-[-12px_0_40px_rgba(0,0,0,0.12)]">
        <header className="grid shrink-0 grid-cols-[44px_1fr_44px] items-center gap-2 border-b border-[#ececec] px-2 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full text-black hover:bg-black/[0.04]"
            aria-label="Close gallery"
          >
            <ChevronLeft className="h-6 w-6" strokeWidth={2} />
          </button>
          <div className="min-w-0 text-center">
            <div className="text-[17px] font-semibold leading-tight text-black">
              Gallery ({items.length})
            </div>
            <div className="mt-0.5 truncate text-[14px] leading-tight text-[#888]">{venueTitle}</div>
          </div>
          <div className="flex h-10 w-10 items-center justify-end">
            <button
              type="button"
              onClick={onClose}
              className="hidden h-10 w-10 items-center justify-center rounded-full text-black hover:bg-black/[0.04] md:flex"
              aria-label="Close gallery"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">
          <div className="mx-auto grid w-full max-w-[640px] grid-cols-1 gap-4 md:max-w-none md:grid-cols-3 md:gap-3">
            {items.map((item, i) => (
              <button
                key={`${i}-${String(item.url).slice(-32)}`}
                type="button"
                onClick={() => openLightbox(i)}
                className="group relative block w-full overflow-hidden rounded-[20px] bg-[#f4f4f4] text-left shadow-sm ring-0 transition active:scale-[0.99] md:rounded-2xl"
              >
                <div className="aspect-[4/3] w-full">
                  {item.type === 'video' ? (
                    <>
                      <ResponsiveVideo
                        src={item.url}
                        poster={item.poster || defaultPoster}
                        deliveryWidth={480}
                        className="h-full w-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                        disablePictureInPicture
                      />
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-black shadow-md">
                          <Play className="ml-0.5 h-6 w-6 fill-current" aria-hidden />
                        </span>
                      </span>
                    </>
                  ) : (
                    <ResponsiveImage
                      src={item.url}
                      variant="card"
                      alt={`Photo ${i + 1}`}
                      className="h-full w-full object-cover transition group-hover:opacity-95"
                      sizes="(max-width: 768px) 92vw, 220px"
                    />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
